"use client";

import { useReducer, useCallback, useRef, useEffect } from 'react';
import { clientLogger } from '@/lib/client-logger';

// ============================================================================
// Types
// ============================================================================

export type MintingStatus =
  | 'idle'
  | 'claiming'
  | 'minting'
  | 'polling'
  | 'success'
  | 'failed'
  | 'requires_wallet'
  // Terminal state: claim was recorded but can't be minted yet because the
  // user has no destination wallet. They can link one later to complete.
  | 'reserved'
  // Terminal state: the mint was accepted (e.g. POAP queued it / claimQr
  // consumed the link) but didn't finalize within our poll window. It's not a
  // failure — the asset will land shortly and the status endpoint reconciles it.
  | 'finalizing';

export interface MintingState {
  status: MintingStatus;
  claimId?: string;
  transactionId?: string;
  transactionUrl?: string;
  mintedAssetId?: string;
  errorMessage?: string;
  canRetry?: boolean;
  retryCount?: number;
}

// ============================================================================
// Action Types
// ============================================================================

type MintingAction =
  | { type: 'START_CLAIM' }
  | { type: 'CLAIM_CREATED'; claimId: string; hasMinting: boolean }
  | { type: 'CLAIM_RESERVED'; claimId: string }
  | { type: 'REQUIRES_WALLET'; errorMessage: string }
  | { type: 'WALLET_CONNECTED' }
  | { type: 'MINTING_SUCCESS'; claimId: string; transactionId?: string; transactionUrl?: string; mintedAssetId?: string }
  | { type: 'MINTING_FAILED'; claimId?: string; errorMessage: string; canRetry?: boolean; retryCount?: number }
  | { type: 'POLLING_TIMEOUT'; errorMessage: string }
  | { type: 'RETRY_START' }
  | { type: 'RESET' };

// ============================================================================
// Reducer
// ============================================================================

function mintingReducer(state: MintingState, action: MintingAction): MintingState {
  switch (action.type) {
    case 'START_CLAIM':
      return {
        status: 'claiming',
      };

    case 'CLAIM_CREATED':
      return {
        status: action.hasMinting ? 'minting' : 'success',
        claimId: action.claimId,
      };

    case 'CLAIM_RESERVED':
      return {
        status: 'reserved',
        claimId: action.claimId,
      };

    case 'REQUIRES_WALLET':
      return {
        status: 'requires_wallet',
        errorMessage: action.errorMessage,
      };

    case 'WALLET_CONNECTED':
      return {
        status: 'idle',
      };

    case 'MINTING_SUCCESS':
      return {
        status: 'success',
        claimId: action.claimId,
        transactionId: action.transactionId,
        transactionUrl: action.transactionUrl,
        mintedAssetId: action.mintedAssetId,
      };

    case 'MINTING_FAILED':
      return {
        status: 'failed',
        claimId: action.claimId,
        errorMessage: action.errorMessage,
        canRetry: action.canRetry,
        retryCount: action.retryCount,
      };

    case 'POLLING_TIMEOUT':
      // Not a failure: the mint was accepted but is still settling. Move to a
      // terminal "finalizing" state so the spinner stops and we show a calm
      // "it'll appear shortly" message instead of an endless spinner.
      return {
        ...state,
        status: 'finalizing',
      };

    case 'RETRY_START':
      return {
        ...state,
        status: 'minting',
        errorMessage: undefined,
      };

    case 'RESET':
      return {
        status: 'idle',
      };

    default:
      return state;
  }
}

// ============================================================================
// Configuration
// ============================================================================

export interface MintingConfig {
  pollIntervalMs?: number;
  maxPollAttempts?: number;
}

const DEFAULT_CONFIG: Required<MintingConfig> = {
  pollIntervalMs: 2000,
  maxPollAttempts: 30,
};

// ============================================================================
// Hook
// ============================================================================

export interface UseMintingMachineOptions {
  clueId: string;
  campaignId: string;
  hasAtomicTemplateId: boolean;
  getOtp: () => string | null;
  getIykRef: () => string;
  getChipId: () => string;
  createDeviceHash: () => string;
  onOwnershipChange?: (owns: boolean, assetId?: string) => void;
  config?: MintingConfig;
}

export interface UseMintingMachineResult {
  state: MintingState;
  startClaim: () => Promise<void>;
  retry: () => Promise<void>;
  reset: () => void;
  walletConnected: () => void;
  isProcessing: boolean;
  canClaim: boolean;
}

export function useMintingMachine({
  clueId,
  campaignId,
  hasAtomicTemplateId,
  getOtp,
  getIykRef,
  getChipId,
  createDeviceHash,
  onOwnershipChange,
  config = {},
}: UseMintingMachineOptions): UseMintingMachineResult {
  const [state, dispatch] = useReducer(mintingReducer, { status: 'idle' });
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { pollIntervalMs, maxPollAttempts } = { ...DEFAULT_CONFIG, ...config };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll claim status
  const pollClaimStatus = useCallback(async (claimId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/claims/${claimId}/status`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check claim status');
      }

      const claim = data.claim;

      if (claim.status === 'CLAIMED') {
        // Success - stop polling
        dispatch({
          type: 'MINTING_SUCCESS',
          claimId,
          transactionId: claim.mintTransactionId,
          transactionUrl: data.transactionUrl,
          mintedAssetId: claim.mintedAssetId,
        });
        onOwnershipChange?.(true, claim.mintedAssetId);
        return true; // Done polling
      }

      if (claim.status === 'RESERVED') {
        // Terminal: claim is held for the user; no mint will land until they
        // link a wallet. Stop polling and show the reserved UI.
        dispatch({ type: 'CLAIM_RESERVED', claimId });
        return true;
      }

      if (claim.status === 'FAILED' || (claim.status === 'PENDING' && claim.errorMessage)) {
        // Failed - stop polling
        dispatch({
          type: 'MINTING_FAILED',
          claimId,
          errorMessage: claim.errorMessage || 'Minting failed. Please try again.',
          canRetry: data.canRetry,
          retryCount: claim.retryCount,
        });
        return true; // Done polling
      }

      // Still PENDING - continue polling
      return false;
    } catch (err) {
      clientLogger.error("Error polling claim status", {
        error: err instanceof Error ? err.message : "Unknown error",
        claimId
      });
      return false; // Continue polling on error
    }
  }, [onOwnershipChange]);

  // Start polling with interval
  const startPolling = useCallback((claimId: string) => {
    let pollCount = 0;

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      const isDone = await pollClaimStatus(claimId);

      if (isDone || pollCount >= maxPollAttempts) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        if (pollCount >= maxPollAttempts && !isDone) {
          dispatch({
            type: 'POLLING_TIMEOUT',
            errorMessage: 'Minting is taking longer than expected. Please check back later.',
          });
        }
      }
    }, pollIntervalMs);

    // Initial poll
    pollClaimStatus(claimId);
  }, [pollClaimStatus, maxPollAttempts, pollIntervalMs]);

  // Start claim process
  const startClaim = useCallback(async () => {
    dispatch({ type: 'START_CLAIM' });

    try {
      const iykRef = getIykRef();
      const storedOtp = getOtp();
      const chipId = getChipId();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (storedOtp) {
        headers['x-iyk-code'] = storedOtp;
      }

      const response = await fetch('/api/claim-asset', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          clueId,
          campaignId,
          chipId,
          refId: iykRef,
          deviceHash: createDeviceHash(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresWallet) {
          dispatch({
            type: 'REQUIRES_WALLET',
            errorMessage: data.error || 'Please connect your WAX wallet to claim this digital asset',
          });
          return;
        }
        throw new Error(data.error || 'Failed to claim asset');
      }

      // Check for minting failure returned in a 200 response (e.g., no mint links)
      if (data.success === false) {
        throw new Error(data.message || data.mintError || 'Claim failed');
      }

      const claimId = data.claim?.id;
      if (!claimId) {
        throw new Error('No claim ID returned');
      }

      // Reserved claim (user has no WAX wallet yet): short-circuit — don't
      // start polling because status won't change until a wallet is linked
      // and a separate mint flow runs.
      if (data.claim?.status === 'RESERVED') {
        dispatch({ type: 'CLAIM_RESERVED', claimId });
        return;
      }

      // Determine if this claim involves minting (WAX template or POAP event)
      const hasMinting = hasAtomicTemplateId || data.mintStatus != null;

      dispatch({
        type: 'CLAIM_CREATED',
        claimId,
        hasMinting,
      });

      if (hasMinting) {
        startPolling(claimId);
      } else {
        // Legacy claim - no minting
        onOwnershipChange?.(true);
      }
    } catch (err) {
      clientLogger.error("Error claiming asset", {
        error: err instanceof Error ? err.message : "Unknown error",
        clueId
      });
      dispatch({
        type: 'MINTING_FAILED',
        errorMessage: err instanceof Error ? err.message : 'An error occurred',
        canRetry: true,
      });
    }
  }, [clueId, campaignId, hasAtomicTemplateId, getOtp, getIykRef, getChipId, createDeviceHash, startPolling, onOwnershipChange]);

  // Retry minting
  const retry = useCallback(async () => {
    if (!state.claimId) {
      // No existing claim - start fresh
      await startClaim();
      return;
    }

    dispatch({ type: 'RETRY_START' });

    try {
      const response = await fetch(`/api/claims/${state.claimId}/retry`, {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to retry minting');
      }

      startPolling(state.claimId);
    } catch (err) {
      clientLogger.error("Error retrying mint", {
        error: err instanceof Error ? err.message : "Unknown error",
        claimId: state.claimId
      });
      dispatch({
        type: 'MINTING_FAILED',
        claimId: state.claimId,
        errorMessage: err instanceof Error ? err.message : 'Retry failed',
        canRetry: true,
      });
    }
  }, [state.claimId, startClaim, startPolling]);

  // Reset state
  const reset = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    dispatch({ type: 'RESET' });
  }, []);

  // Wallet connected - transition from requires_wallet back to idle
  const walletConnected = useCallback(() => {
    dispatch({ type: 'WALLET_CONNECTED' });
  }, []);

  const isProcessing = ['claiming', 'minting', 'polling'].includes(state.status);
  const canClaim = state.status === 'idle' || state.status === 'failed' || state.status === 'requires_wallet';

  return {
    state,
    startClaim,
    retry,
    reset,
    walletConnected,
    isProcessing,
    canClaim,
  };
}
